import { useParams } from 'next/navigation';
import Typography from '@/components/atoms/typography';
import { BOWL_POSITION, CAPACITY_SIZE, CAPACITY_TO_OVERFLOW, CARTRIDGE_SIZE, CARTRIDGE_TYPE, CLEAN_FLUSH, INDEPENDENT_LIVING_COMPLAINT, INLET_CONNECTION, INLET_TYPE, ISAMBULANT, ISCOMPLIANT, ISNZCOMPLAINT, ITEM_NAME, ITEM_TAPHOLES, LITRES_PER_MINUTE, MAXIMUM_OPERATING_PRESSURE, MAXIMUM_OPERATING_TEMPERATURE, MINIMUM_OPERATING_PRESSURE, MINIMUM_OPERATING_TEMPERATURE, MOUNT_TYPE, NOMINAL_FACTORY_PRESSURE_SETTING, NOMINAL_FACTORY_TEMPERATURE_SETTING, OUTLET_CONNECTION, OUTLET_LENGTH, OUTLET_TYPE, OVERFLOW, PAN_HEIGHT, PAN_PROJECTION, RAIL_LENGTH, SHAPE, SHOWER_HEAD_SIZE, SPRAY_FUNCTIONS, SURFACE_FINISH, TAPHOLES, TRAP_TYPE, VALVE_TYPE, WASTE_POSITION, WELS_FLOWRATE_1, WELS_FLOWRATE_2 } from '@/constants/attributes';
import { ArrowDownTrayIcon  } from '@heroicons/react/24/outline';
import Link from 'next/link';
import * as React from 'react';
import {  useEffect, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import EmailAllFilesPopup from '../../emailAllFilesPopup';
import useAccount from '@/lib/hooks/useAccount';
import Button from '@/components/atoms/button';

interface ProductAttributes {
  assetsAttributes?: Array<{ name: string; value: string }>;
  attributes: any;
  variants: any;
  product: any;
  setLoading?: any;
}

const ProductSpecificationDetails = ({ attributes, variants, product, setLoading }: ProductAttributes) => {

  const safeRenderValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object' && value.label) return value.label;
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
  };

  const isValidValue = (val: any) => {
    if (val === undefined || val === null || val === '' || val === 0 || val === '0') return false;
    if (typeof val === 'string') {
      const v = val.trim().toLowerCase();
      if (['na', 'n/a', 'not applicable', '0', '0 mm', '0mm', '0 l', '0l'].includes(v)) return false;
    }
    return true;
  };

  const technicalImageURL = attributes['assets-attributes']?.find(
    (attribute: { name: string }) => attribute.name === 'technical-image',
  )?.value;
  const getAttributeValueLength = (attributes: any, key: any) =>
    attributes['assets-attributes']?.find((attr: { name: string; value: string }) => attr.name === key)?.value?.length || 0;

  const productSubCategory = product?.categories?.[0]?.categoryId ?? '';
  const productCategory = product?.categories?.[0]?.parentKey ?? '';
  const isSanitaryware = productCategory
    ?.toLowerCase()
    ?.includes('sanitary');
  const getKeyWelsFowRateUpdatedKey = (welsKeyNumber: number) => {
    if (variants && variants.attributes?.[WELS_FLOWRATE_1] && variants.attributes?.[WELS_FLOWRATE_2]) {
      return `Wels With flow Rate #${welsKeyNumber}`;
    }
    else {
      return 'Wels With flow Rate';
    }
  };


  const { account } = useAccount();
  //Checking whether user is GWA or now
  const documents = getAttributeValueLength(attributes, 'installation-guidelines-set') > 0;
  const technicalDocuments = getAttributeValueLength(attributes, 'technical-manual-set') > 0;
  const ExcludedNames = [
    'warranty-Info',
    documents ? 'installation-guidelines' : 'installation-guidelines-set',
    technicalDocuments ? 'technical-manual' : 'technical-manual-set'
  ];
  // const filteredNames = attributes['assets-attributes']
  //   ?.filter((item: any) => !ExcludedNames?.includes(item?.name))
  //   ?.map((item: any) => item);
  const downloadOrder = [
    'technical-image',
    'installation-guidelines',
    'installation-guidelines-set',
    'installation-guidelines-static',
    'technical-manual',
    'technical-manual-set',
    'BOM-file',
  ];
  // Hardcoded installation guide override for PHYPF011-AU (no dynamic asset exists for this item yet)
  const PHYN_INSTALL_GUIDE_ITEM_CODE = 'PHYPF011-AU';
  const PHYN_INSTALL_GUIDE_URL =
    'https://assets.contentstack.io/v3/assets/bltad292a6aa65b6a42/bltad68ef256088fbf8/6aea53061a6f6edd9853c4eb/InstallGuide-LeakSmartShield.pdf';
  const assetsAttributesList =
    attributes?.['item-code'] === PHYN_INSTALL_GUIDE_ITEM_CODE
      ? [...(attributes['assets-attributes'] || []), { name: 'installation-guidelines-static', value: PHYN_INSTALL_GUIDE_URL }]
      : attributes['assets-attributes'];
  const filteredNames = assetsAttributesList
    ?.filter((item: any) =>
      !ExcludedNames?.includes(item?.name) &&
      (item?.name !== 'test-report')
    )
    ?.map((item: any) => item)
    ?.sort((a: any, b: any) => {
      const ai = downloadOrder.indexOf(a.name);
      const bi = downloadOrder.indexOf(b.name);
      const aOrder = ai === -1 ? downloadOrder.length : ai;
      const bOrder = bi === -1 ? downloadOrder.length : bi;
      return aOrder - bOrder;
    });

  const DisplayDownloadComp = filteredNames?.some((item: { value: any }) => item.value !== '' && !(Array.isArray(item.value) && item.value.length === 0));
  const [files, setFiles] = useState(filteredNames);
  const productType = attributes?.['product-types'] === 'Brochures';

  const downloadAllFiles = async () => {
    try {
      setLoading(true);
      const zip = new JSZip();

      // Helper function to download files
      const downloadFile = async (name: string, url: string) => {
        try {
          if (!url) {
            console.warn(`Skipping empty URL for file: ${name}`);
            return null;
          }

          // Encode the URL to handle spaces and special characters
          const fileUrl = encodeURI(url.replaceAll('ProductThumbnail', 'Original'));
          const response = await fetch(fileUrl);
          if (!response.ok) {
            console.error(`Failed to fetch file from URL: ${fileUrl} - Status: ${response.status}`);
            return null;
          }
          const content = await response.blob();
          const filename = name.includes('.') ? name : getFilenameFromUrl(fileUrl);
          return { filename, content };
        } catch (error) {
          console.error(`Error fetching file: ${url}`, error);
          return null;
        }
      };
      // Filter out empty/invalid file values
      const validFiles = files.flatMap((file: { name: string; value: string | string[] }) => {
        if (Array.isArray(file.value)) {
          return file.value.map(url => ({ name: file.name, url }));
        } else if (file.value) {
          return [{ name: file.name, url: file.value }];
        }
        return [];
      });
      if (validFiles.length === 0) {
        console.warn('No valid files to download.');
        setLoading(false);
        return;
      }
      // Create promises for all file downloads
      const filePromises = validFiles.map(({ name, url }: any) => downloadFile(name, url));
      // Wait for all promises to settle
      const results = await Promise.allSettled(filePromises);
      // Process successful downloads
      results
        .filter(result => result.status === 'fulfilled' && result.value !== null)
        .forEach((result) => {
          const { filename, content } = (result as PromiseFulfilledResult<any>).value;
          zip.file(filename, content);
        });

      // Generate the ZIP if there are files
      if (Object.keys(zip.files).length > 0) {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, 'files.zip');
      } else {
        console.warn('No files were successfully downloaded to create the ZIP.');
      }
    } catch (error) {
      console.error('Error downloading files as ZIP:', error);
    } finally {
      setLoading(false);
    }
  };
  const getFilenameFromUrl = (url: string) => {
    const parts = url.split('/');
    return parts[parts.length - 1];
  };
 const HIDDEN_FOR_TOILET_SEATS = [
  TRAP_TYPE,
  INLET_TYPE,
  PAN_HEIGHT,
  PAN_PROJECTION,
  CLEAN_FLUSH,
];
const isToiletSeat =
  productSubCategory?.toLowerCase()?.includes("toilet") &&
  productSubCategory?.toLowerCase()?.includes("seat");
  //svg for downloaded files
  const FILE_DOWNLOADS = [
    {
      name: 'DAE-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
          className='print:w-[40%]'
          fill="none"
        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36603)" />
          <defs>
            <pattern id="pattern0_12104_36603" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36603" transform="scale(0.02)" />
            </pattern>
            <image
              id="image0_12104_36603"
              width="50"
              height="50"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA+NJREFUeNrsmc9LG0EUxzebH11jpCvEohA1hVJqT7kJQbD2XKiFIj0UGj0r2ksvFpKA97bgWSP2IKHQ9C9IBC/eIhTsSbYQMGDRRWLMLzf9TjoL0yWJ0Ww2u5iBx/6anZnPvPfmvZ3luF4xV7FdV2F/fz+iQz9pSGpyclLuJkhVx/5ikPedAGoVJIrOb60ZtCHiMAv5BJEg82gvrScIb4T9Eg1AiDZm6K0k4AKWA2GA0hRG0huGN3p1of7BwsxaEkQDk4J8B0zIkiCM37yiK9lmuzBdA2GA5hmYiGVBGJgoJAyYzdu04ejEwFZXP3orlYrP4/EIo6NjT8g9p9P++PT0NL60tJRuABMBhEQ1o8IZA7K2tjbr842JGOQjt9vtcTgcwzzPDwuCwAHiv7r5fF7w+/3BRCKxd3BwsB4Oh7N1YGIEQoW5SRbQFsjExMTz/v7+oNPprGDghWs7czi4kZGRKVEUp+Lx+LfDw8MYgHINYEgWEMD5TCswbfkIZl8ZHBzMuVwu5ezszANzaqm9vr4+bnx8/HUwGNzZ3t5+G41GBS0MXZ79NNaIhjg7QEoE6PLy0iXLspt9dnV1xQNSOD8/v4ejm5yrx3K5/MDr9S6i2tMmWYCfasc4Zx8YGCgoisJnMpn78JOy3W5XIBwgC/ARDn7UyPyURikNtBGlIPOGrlrE3OA3RTJ4nZpsKUs2RRyxbK7VA+mB9ECMLx1JGhHh7SS3YoIiiRP2UqkkIHgWTAeSy+VcZMDVapW32WwK1bCCa1ud4FcCkAPR34PcrIC8q2Ia0zo6OvpwcnLyFUEwTwYOqR1JElmvPqJ8BXlWDqA1IJIFmAKEZK5zc3Pru7u7b7LZbBom1dJ7RCMECLmWiwDp4au6+Aj9tljZ2NiY8vl8izTRq5VischdXFxwmH1wVn5Sk/yFQ0GSpAzu/SHKNZWzLyws7CElTw8NDb2ADxSOj4/JF58MUMlyqxb9UNoxVRxBCr1i+ThCIdQPmmmdfi/ctKT0MC328/IZFc6sMHc2Ran7n4RuDhBTDNNbEuo9ZJ6TTbdQszZovapRIGqHxMyS9DJB93DJBttLsoXD/ds1rPkZnn2m1yFNGwHNpoLYjkb0MC32t4BMtUH2pmq7H+S3Aa5JHIlp3pMooCpb3YgjMuOEXxjzCjC7Hex/EK6OVvyMKbZdbE3MJ1Kno1iDmSMAy/RcBWHf3aJAyxSw0ewntT5JJywJrdr01EhIa+stDIars3SH9F617sTyG1PV2sSkOl0kNpO+FQhdaSS6M/4b16luzDRd/XqmpS3vMDPTXRqjqBdI1AQT/oPrFYuVvwIMALy8tBM68C9KAAAAAElFTkSuQmCC"
            />
          </defs>
        </svg>
      ),
      value: 'DAE-file',
    },
    {
      name: 'RFA-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
          fill="none"
        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36592)" />
          <defs>
            <pattern id="pattern0_12104_36592" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36592" transform="scale(0.02)" />
            </pattern>
            <image
              id="image0_12104_36592"
              width="50"
              height="50"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAxFJREFUeNrsWtFR4zAQNQwFuAN8FZzzkW/iCuAqIK6ApII4FSRUEKiAXAUx3/nAHeASTAXcLrO+0QhrJVuyFBh2ZieDLdn79q12VzJR9COnJWe6AcfjsXDwngq0nE6nTUgg7w7f9wC6HAPQuecImIMewDlpKCB1j2fi2D1p17x0DDCmobUGvQVNFMNK0HtcCxA2tTR/AT8r0Fiag+GVwfjKd2itmXsJGLSXQaDAtS0a3DEnJmZuvAIBgx6YEEvAoDkzt1I4AsE8cXPHWuwcKyuNIwoKpy7Z2YLpBcSGFSH9RgyYwhcjVqyAvGmeuwIwOy9ALFm51oRejrVmCJihBbE3K2ScqnY0gpP+gwGNndcR8ph4/ZWpKznVlpSUq0EfaweenwvPRlY3xHxm0tLYtCgcK+h9BPpEDHEgGvlZxExG8w4mzAwGolkrfWSpKKSVAGYzdtO4tu2GySG6QjofFYgDVkwWdTVm1nLJipPW3hqIAStteJTMmLa1nwcDYtIZg24BcKYZF1ObsgkGRMMKGrgQqvcfpnlEWQCYF9Ak1FaX8/Zdu6Bx3wI/E80ixlBDMDPvQExZobE16ETTDX9svNAJIQ4fjFgRAOXUznChduMdSB9WOtoRq737GMdBeR9WpHZkfzJAwKiSqRmdrNC8BhQz2vJUGOm9VhSnLk1wIENZkeb/0nQDXhixYkUItcw01M41u8NFKFb6dr9nGhBt31MPbNcTZnfYGBj5SO89gGPYbfmFprKaGDRU8Pm6FuTZ1IEXDmP/mnqkkgyQ5VLY6W2FM6676PMBt5dzrYK62NZTFV2b0LVnuq/qo/DL1ZJa+2Jo3XCZtX6DXpFG1Klynp1JjOLhdeLqEMMGyCMZda9YV12Cnx5KYa+ROtouWwHZUJu9E9KtnLFWgiZCGMVkfIP1hFixahq59FtE3cefpeDNWEqjKf1d071UkW5nUp1oFJmx3etbpV9drKvSaKxI1ap0m4Ze7F9L8BDb0T8NDH3/zORb/7dh5NsAMV3st0DvVSAbY1dA1ifg8L/Rj3wx+SfAAB1PSfhKvw1eAAAAAElFTkSuQmCC"
            />
          </defs>
        </svg>
      ),
      value: 'RFA-file',
    },
    {
      name: 'SKP-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
          fill="none"
        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36608)" />
          <defs>
            <pattern id="pattern0_12104_36608" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36608" transform="scale(0.0166667)" />
            </pattern>
            <image
              id="image0_12104_36608"
              width="60"
              height="60"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADwAAAA8CAYAAAA6/NlyAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA8FJREFUeNrsmlFu2kAQhheLUkpSCUVtVVWVcG4AT3mFE5ScIOYEdU+QcAKTE+CcAHIC6CNPOCcIT1XVh4iqkeogSjsTjenWNWCbXbw4HmmFsY3x5/lnZr27jGWWWaosF+ak0WhUh4+BwP+dUHOgXZ6cnExUBcYbnAr67zK0Km13oLUBfKoacANuaijqz+G6OnxY0Jr0MBuyobUk4wmlDO0UNlvk7QE8hHJqgTlwm4MeA3Q11cA+aJ08XU0dMMoXWhcaerVM0DU6LAVaSxBWp0RokJQt8vRj8uKgjb0HBgjMymOuLKEZsN8MgO6KhNYSgEVP9qgO+83yZEzQx1SuhEFrO45XlLC54dRlaaKa3OCgrb0AJq/dQquH7IENuOzNQ5uY5JQGprgcr5DwKqvy3kRoaJi9bYr1rpLAdGNxZWj64xagWxx0L06vTJMcr9smGstfiznoZpyuqCYDlmKwLuByeK3/PEnQn+L0vzUJyclfX7c1Peh6AN3h+t+9nQMT7IBucJf97zaqKWw3VBMEa8TIxKJsyMlfPjDBdtmemAgPf2B7ZCKAb4J2/nRd9uP+ni0WC6WA87Iu7LozdjfFXuEde3lwyN68PkqNhwOtWCwst+e/5qmS9DBo57N8nj0vFJSLYWmSzgPw+3dv2Xw+Zw+zWfqBeXBsaZK086TK0rqZAixJguQ83QtJI+yXr98eJf3qqMwOSqU4l+nT+JZSZWmy7iAmrtlsHve6LRXr8ESSSFqiJ9ekDvG8KBa3+XlH5EylaGAcfbAFJheH3nPV7FpiUqFhl2OKOU/i9mGphKONHdd9mCQp5ahZWg8JPiVP2zgCwWVXB75fs3DLJtois3JkD9P4cDeO1wMSW3+TlOF3FzLzSm4DbN3nFRFrPPQ1iplEzPhXdH7o5RhROx5VJtd0Fm0Q8HPUkqjMCgDV35baVCenvhBA7xjQzmnXUmZw7DeXlC5oX5PyA87+51aEFY5GYmhZTMAQcBxgm7thk27Ey87fuVivBrwAnOIDIAh8KGYA4HI1AMqVyl0f9uP33rbAcSRd5+ZvO+QZXHp0TccrK2K9xiUVk22eJ0YrqyBplNUtQDuUNDyPOpznr4Jqrleb8TzYrrCAyTbfcge+WpwnBYy19JLzwEdoZxTX9pr31zHdfMPrmZFMjQ2lMHEPY6K5oVj+pzNBUm+u8IYnT5wNrNEkdyC0Sh0P1QxVNYzS8cjqcEApGbK/S31FLh9OZOAh0eXDIizqvT05SWcxvMHOSEIqWUUmsMEyyywzleyPAAMAXIGyZOQTkgkAAAAASUVORK5CYII="
            />
          </defs>
        </svg>
      ),
      value: 'SKP-file',
    },
    {
      name: 'DXF-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
          fill="none"
        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36614)" />
          <defs>
            <pattern id="pattern0_12104_36614" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36614" transform="scale(0.02)" />
            </pattern>
            <image
              id="image0_12104_36614"
              width="50"
              height="50"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAypJREFUeNrsWe1x4jAQFUwKcAehg4Nf/IxdQUgFQAVABUAFkAogFcRXgZ2f/KOD+DrgKsjt3j0nOsayPpCFbw7NaIyNZenp7b5dSULcSrtKR/fC4XBYeejnSDUfDoenawL58NjfnuqiCUCmQNbUuTMz9I2ILiOqG6oF1Sl97+gTSDeE/TIDVJmNBI8yAtf32cedrw/RwHjGXzV+kqBmAJP4YsYnIxvN/8zAK/wjgYllmIB2AKHBxHTpGbwas0lJYHIGR88mbWFkafHuTPKbJyjZ7lIwXQ9sMBOxRZMJVKwUgqkEZnVNRpYObeZnqsZg1vwtArMLDgQz62IS4wqJZjamYGwXmpG5Y7telU8g1nyCkU2wMSDoZFbzysKWlTMwI8hz1DQj3JGqkz0NaItYUSvFNWASSLoRmG5DTv58dq2VYgWYowRm0wgQRGNVAMyltINn9mQqxQowaxNBcWWkzjfWcrJIl/RCwTg24iOw67iGjVwFzMbpQ8jvzMA3ZPMokFNZSXGjQJCOqDotaNCpKUDfrNgyMjHxjQpWUlcpbgrIYw0be03b5wtM1juQyHGQJlI8sklJLl3qvigC4ZgG8ahpe4KUxjWTxKa7DQFkCxOIKpaxdSWVcq93jXltGzctBLiFRRN28CdeCbIMG0rxKISPlAldYcjeoEKSG3F61xRlqkkpGEDljqKhFPeCAEEaklc4M+9IDgz2qnSsLEMxUrJSzngOFkw3D7xLcVeTksxrWGHzGPCagX4nuLcRjVQTr6zyr44GxEaa8Tfht9xrBltIvpgR+I5rHJGpjS33rnyUHvrMQ+yitKbYRnZeOCUKU5yIr/OPz+MD8edgJ8f/O6E4a8H+cRYKiNzxh2TL7Ox7evYNS9cMzwYSyJ3PgXsDcmbLPQD6efbsqIrWiEUdCdhKuG2/XgykdMI3aZ3+gPjw+wAHTKTi63Cn3xZGIgzgrxUhBjyGyizwXinfMzxb4P4e37jq0VvfYBY3mjZ94bbxfZPfPfwgw++XK4yvEGZHemogyJ0Ksme+/VGx8RakmKb0/11k582FhyuNMfIFZN2CCf8ubuUfK78EGADz81F09iyzbgAAAABJRU5ErkJggg=="
            />
          </defs>
        </svg>
      ),
      value: 'DXF-file',
    },
    {
      name: 'OBJ-file',
      icon: (
        <svg width="50" height="50" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M16.3572 6.67143H40.2717L59.3286 23.7569V64.4143H16.3572V6.67143Z"
            stroke="#BDBDBD"
            strokeWidth="2.34286"
          />
          <rect x="13" y="46.2858" width="42.9714" height="13.4286" fill="#BDBDBD" />
          <path
            d="M21.5169 49.6847C22.2499 49.0951 23.218 48.8003 24.4211 48.8003C25.6242 48.8003 26.5883 49.099 27.3133 49.6966C28.0463 50.2862 28.4129 51.1188 28.4129 52.1945V54.0589C28.4129 55.1504 28.0503 55.9831 27.3253 56.5567C26.6082 57.1224 25.6401 57.4053 24.4211 57.4053C23.202 57.4053 22.23 57.1224 21.5049 56.5567C20.7878 55.9831 20.4293 55.1504 20.4293 54.0589V52.1945C20.4293 51.1109 20.7918 50.2743 21.5169 49.6847ZM23.7757 54.8357C23.9351 54.9951 24.1502 55.0748 24.4211 55.0748C24.692 55.0748 24.9071 54.9951 25.0665 54.8357C25.2258 54.6764 25.3055 54.4334 25.3055 54.1067V52.0988C25.3055 51.7801 25.2258 51.5411 25.0665 51.3818C24.9071 51.2144 24.692 51.1308 24.4211 51.1308C24.1502 51.1308 23.9351 51.2144 23.7757 51.3818C23.6164 51.5411 23.5367 51.7801 23.5367 52.0988V54.1067C23.5367 54.4334 23.6164 54.6764 23.7757 54.8357ZM34.9122 51.9076C34.9998 51.804 35.0437 51.6805 35.0437 51.5371C35.0437 51.3937 34.9998 51.2662 34.9122 51.1547C34.8325 51.0352 34.713 50.9754 34.5537 50.9754H33.5497V52.063H34.5537C34.713 52.063 34.8325 52.0112 34.9122 51.9076ZM33.5497 54.0589V55.2301H34.6612C34.8445 55.2301 34.9799 55.1704 35.0676 55.0508C35.1632 54.9234 35.211 54.7839 35.211 54.6325V54.6206C35.211 54.4692 35.1592 54.3377 35.0556 54.2262C34.96 54.1147 34.8285 54.0589 34.6612 54.0589H33.5497ZM34.972 57.2858H30.8846C30.7969 57.2858 30.7212 57.2539 30.6575 57.1902C30.5937 57.1264 30.5619 57.0507 30.5619 56.9631V49.2425C30.5619 49.1548 30.5937 49.0791 30.6575 49.0154C30.7212 48.9516 30.7969 48.9198 30.8846 48.9198H34.8524C35.9838 48.9198 36.8165 49.1269 37.3503 49.5412C37.8921 49.9476 38.163 50.5651 38.163 51.3937V51.4296C38.163 51.7722 38.0634 52.0789 37.8642 52.3498C37.665 52.6128 37.4459 52.804 37.2069 52.9235C37.5256 53.051 37.7925 53.2781 38.0076 53.6047C38.2307 53.9234 38.3423 54.274 38.3423 54.6565C38.3423 56.4093 37.2188 57.2858 34.972 57.2858ZM47.8181 49.2425V54.1425C47.8181 55.2261 47.4555 56.0428 46.7305 56.5926C46.0134 57.1344 45.1051 57.4053 44.0055 57.4053C42.906 57.4053 41.9858 57.1423 41.2448 56.6165C40.5038 56.0906 40.1333 55.3775 40.1333 54.4772C40.1333 54.4055 40.1572 54.3457 40.205 54.2979C40.2608 54.2421 40.3245 54.2142 40.3962 54.2142H42.8343C42.9618 54.2142 43.0574 54.2461 43.1211 54.3099C43.1929 54.3656 43.2646 54.4732 43.3363 54.6325C43.408 54.7839 43.4916 54.8955 43.5872 54.9672C43.6908 55.0389 43.8342 55.0748 44.0175 55.0748C44.4876 55.0748 44.7226 54.7521 44.7226 54.1067V51.3459H41.1731C41.0854 51.3459 41.0097 51.314 40.946 51.2503C40.8822 51.1866 40.8504 51.1109 40.8504 51.0232V49.2425C40.8504 49.1548 40.8822 49.0791 40.946 49.0154C41.0097 48.9516 41.0854 48.9198 41.1731 48.9198H47.4954C47.583 48.9198 47.6587 48.9516 47.7224 49.0154C47.7862 49.0791 47.8181 49.1548 47.8181 49.2425Z"
            fill="#E4E4E4"
          />
          <path
            d="M43 30H29M43 30C44.1046 30 45 30.8954 45 32V38C45 39.1046 44.1046 40 43 40H29C27.8954 40 27 39.1046 27 38V32C27 30.8954 27.8954 30 29 30M43 30V28C43 26.8954 42.1046 26 41 26M29 30V28C29 26.8954 29.8954 26 31 26M31 26V24C31 22.8954 31.8954 22 33 22H39C40.1046 22 41 22.8954 41 24V26M31 26H41"
            stroke="#BDBDBD"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M33.3002 14.2454C33.0904 14.0487 33.0904 13.7299 33.3002 13.5332L35.4488 11.519C35.6585 11.3223 35.9986 11.3223 36.2084 11.519L38.357 13.5332C38.5668 13.7299 38.5668 14.0487 38.357 14.2454C38.1472 14.4421 37.8071 14.4421 37.5974 14.2454L36.3657 13.0908L36.3657 18.925C36.3657 19.2031 36.1253 19.4286 35.8286 19.4286C35.5319 19.4286 35.2915 19.2031 35.2915 18.925L35.2915 13.0908L34.0598 14.2454C33.8501 14.4421 33.51 14.4421 33.3002 14.2454Z"
            fill="#BDBDBD"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M52.538 37.5839C52.8228 37.5447 53.0706 37.7453 53.0914 38.0321L53.3049 40.9694C53.3257 41.2562 53.1117 41.5205 52.8269 41.5598L49.9094 41.9621C49.6245 42.0014 49.3767 41.8008 49.3559 41.514C49.335 41.2272 49.5491 40.9629 49.8339 40.9236L51.5063 40.693L46.972 37.0217C46.7558 36.8467 46.7319 36.5179 46.9186 36.2873C47.1053 36.0568 47.4318 36.0117 47.648 36.1867L52.1823 39.8581L52.0599 38.1743C52.0391 37.8875 52.2531 37.6232 52.538 37.5839Z"
            fill="#BDBDBD"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M23.8766 41.5107C23.8545 41.7974 23.6059 41.997 23.3212 41.9566L20.4054 41.542C20.1208 41.5015 19.9079 41.2363 19.9299 40.9496L20.1557 38.0131C20.1778 37.7264 20.4264 37.5268 20.7111 37.5673C20.9958 37.6078 21.2087 37.873 21.1866 38.1597L21.0572 39.8429L25.6069 36.1907C25.8238 36.0166 26.1501 36.063 26.3359 36.2944C26.5216 36.5257 26.4963 36.8544 26.2794 37.0285L21.7297 40.6807L23.4011 40.9184C23.6857 40.9588 23.8986 41.2241 23.8766 41.5107Z"
            fill="#BDBDBD"
          />
        </svg>
      ),
      value: 'OBJ-file',
    },
    {
      name: 'DWG-file',
      icon: (
        <svg width="50" height="50" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M16.3572 6.67143H40.2717L59.3286 23.7569V64.4143H16.3572V6.67143Z"
            stroke="#BDBDBD"
            strokeWidth="2.34286"
          />
          <rect x="13" y="46.2858" width="42.9714" height="13.4286" fill="#BDBDBD" />
          <path
            d="M20.0807 48.9198H23.5227C24.6859 48.9198 25.6421 49.2106 26.391 49.7922C27.1479 50.3659 27.5264 51.1706 27.5264 52.2064V53.9872C27.5264 55.0708 27.1599 55.8914 26.4269 56.4492C25.6938 57.0069 24.7298 57.2858 23.5346 57.2858H20.0807C19.993 57.2858 19.9173 57.2539 19.8536 57.1902C19.7898 57.1264 19.758 57.0507 19.758 56.9631V49.2425C19.758 49.1548 19.7898 49.0791 19.8536 49.0154C19.9173 48.9516 19.993 48.9198 20.0807 48.9198ZM22.7458 51.2503V54.9552H23.5227C23.7936 54.9552 24.0087 54.8756 24.168 54.7162C24.3354 54.5489 24.419 54.3059 24.419 53.9872V52.2064C24.419 51.569 24.1202 51.2503 23.5227 51.2503H22.7458ZM32.6747 57.2858H30.9656C30.8142 57.2858 30.6867 57.2419 30.5832 57.1543C30.4876 57.0587 30.4318 56.9511 30.4158 56.8316L29.3163 49.2186V49.1827C29.3163 49.111 29.3402 49.0512 29.388 49.0034C29.4438 48.9477 29.5075 48.9198 29.5792 48.9198H31.8022C32.0651 48.9198 32.2086 49.0273 32.2325 49.2425L32.6388 52.6128L33.1288 51.1188C33.2085 50.8878 33.3439 50.7722 33.5352 50.7722H34.7542C34.9454 50.7722 35.0809 50.8878 35.1606 51.1188L35.6506 52.6008L36.0569 49.2425C36.0808 49.0273 36.2242 48.9198 36.4872 48.9198H38.7101C38.7818 48.9198 38.8416 48.9477 38.8894 49.0034C38.9452 49.0512 38.9731 49.111 38.9731 49.1827V49.2186L37.8735 56.8316C37.8576 56.9511 37.7978 57.0587 37.6943 57.1543C37.5986 57.2419 37.4752 57.2858 37.3238 57.2858H35.6147C35.3677 57.2858 35.2044 57.1782 35.1247 56.9631L34.1447 54.513L33.1647 56.9631C33.085 57.1782 32.9217 57.2858 32.6747 57.2858ZM45.2945 52.2423H48.5812C48.6688 52.2423 48.7445 52.2741 48.8082 52.3379C48.872 52.4016 48.9039 52.4773 48.9039 52.565V54.1186C48.9039 55.1704 48.5294 55.9831 47.7804 56.5567C47.0315 57.1224 46.0315 57.4053 44.7806 57.4053C43.5377 57.4053 42.5577 57.1304 41.8406 56.5806C41.1235 56.0229 40.7649 55.1903 40.7649 54.0828V52.0749C40.7649 51.0153 41.1314 50.2065 41.8645 49.6488C42.5975 49.0831 43.5735 48.8003 44.7926 48.8003C46.0196 48.8003 47.0116 49.0592 47.7685 49.5771C48.5254 50.087 48.9039 50.7165 48.9039 51.4654C48.9039 51.5371 48.876 51.6009 48.8202 51.6566C48.7724 51.7044 48.7126 51.7284 48.6409 51.7284H45.9997C45.8403 51.7284 45.7248 51.6845 45.6531 51.5969C45.4937 51.3897 45.3742 51.2702 45.2945 51.2383C45.1113 51.1666 44.944 51.1308 44.7926 51.1308C44.1791 51.1308 43.8723 51.4296 43.8723 52.0271V54.0589C43.8723 54.7361 44.199 55.0748 44.8523 55.0748C45.5216 55.0748 45.8562 54.7879 45.8562 54.2142H45.2945C45.2069 54.2142 45.1312 54.1824 45.0675 54.1186C45.0037 54.0549 44.9718 53.9792 44.9718 53.8916V52.565C44.9718 52.4773 45.0037 52.4016 45.0675 52.3379C45.1312 52.2741 45.2069 52.2423 45.2945 52.2423Z"
            fill="#E4E4E4"
          />
          <rect x="24.5" y="15.5" width="7" height="7" stroke="#BDBDBD" />
          <line x1="27.5" y1="15" x2="27.5" y2="7" stroke="#BDBDBD" />
          <line x1="31" y1="19.5" x2="54" y2="19.5" stroke="#BDBDBD" />
          <line x1="24" y1="19.5" x2="17" y2="19.5" stroke="#BDBDBD" />
          <line x1="27.5" y1="23" x2="27.5" y2="43" stroke="#BDBDBD" />
        </svg>
      ),
      value: 'DWG-file',
    },
    {
      name: '3DS-file',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="50"
          height="50"
          viewBox="0 0 50 50"
          fill="none"
        >
          <rect width="50" height="50" fill="url(#pattern0_12104_36598)" />
          <defs>
            <pattern id="pattern0_12104_36598" patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref="#image0_12104_36598" transform="scale(0.02)" />
            </pattern>
            <image
              id="image0_12104_36598"
              width="50"
              height="50"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAABAZJREFUeNrsWctx4kAQFS7dV5sBjgA4+WgRAZABisBWBJgIgAjAEdhEgDj6ZDkCyxnIEex2bz1R7akWEqMP7K67qksWRqN505/X3TjOt1yWdMp86eXl5aGGd8Wk0c3NTXpOIL9qfOeGNKwb0ClAhvTyyPZFtIZHl3vSO9IE69UG5qotH+ZNk7KLDkm7pO8Erl/X+m4bILBhD7ce3Iuts6P/sWXiiwFCG+JT9kl7pH2cerfgMa8uMG4NAKbwe1s3YTCvtE5AYDatA4G7rI8ASJFyWT8LlmMrrmlNxxaMW8EKa+VfnI1W4Iv4xDXXVcC4lrGggWBuWCrf7QoX6isW440z8IBBAEyP7sOmLXKnfHbwb2x+RjoWmarocDLmZ2u+kS6YdxhckzziG/dLAYJP/JV0WhaEkCzmRmxdXgPu1phFzOBeCebeHQGQQDU30w4qgJuxhSdFVYBrkam+FIL0gmxzeVaIED+xst4YFpgqYB5RBTwJrknrci1P2WQmt0r65dg5kB2fLjaflS3PiIPrLPBlLKK2y0qaRZO11kcOyBRFYRY7Pvz9XbMarDrEcwcXRsDHKGnGTQKJc5LASljhHu4xVaz4pahEkGvx+FmUPJqqfmcE4IktwdxC+pM+G8DVkiMV8sa0yiVUv+wKYwKTCsvt0W1GR3qbSLiRdwlAZOz4hvvNAHCFHkXKW1E8NO1asQVABrQDB7XaIZr+LfuNbYnnlwpg30itvcaBKIHaK9ikabEViC5WKua8Eqgx13rWXoqenDPTPOe5LnhkIbIRZ6mBSNVmdRA3CWRrkJZvWO0BTB0iA6VGBopgvWtm9azsQJwsbOPOtbSIrEqfzJ4bLriElh0VmQVncoxzKlsEJ7hReu71qeMdBgB3elfI77GNnn2uNE5T9BAZAe5lF2iU7z9wzQvsWOGX+oGwyWnDIXzayyFA3/KQYhSQ7RAi6qKBkcWqEioH/8BmlOqW8OOHAl6ZIFh9uMttASfEIpPtwSPRKYGtSafEiGYqcv6H075EOJAZge3YWqRrBPPFinvGd5sZrW8xebEO9hQTjQ7Y+xp/D0Tnx+TYMdXoy0M0WwFSeYB7s91tzCIemPzQsnIaZlan61YGN5LETA7w6LMJyG8jrMDJgYcSiWM/CK/Uj8yhCRj99sh3R0b1vAPQFLVbgBHpJK+nrxvIEuYPcdp/RqOos1ZK7xKBZ+ZijiULzxEGE6+i/NnaAClKvzuDC1JsOFKy2wzXMKdqHYkhXoR1uPSPxLCb33VnBH3WFlRKv2p7Knxfk0WJdQ4lDOKtslw5/4gUWSSAyXdHXKZpScqQsVuiR0+y3y+q/M5eRcq433/jWl+CGE3TOaRbF5D5mQ9873zLXya/BRgAlBrILT3I1R4AAAAASUVORK5CYII="
            />
          </defs>
        </svg>
      ),
      value: '3DS-file',
    },
    {
      name: "BOM-file",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 72 72" fill="none">
          <path d="M16.3572 6.67143H40.2717L59.3286 23.7569V64.4143H16.3572V6.67143Z" stroke="#BDBDBD" strokeWidth="1.34286" />
          <rect x="13" y="46.2858" width="42.9714" height="13.4286" fill="#BDBDBD" />
          <path d="M24.225 51.9076C24.3126 51.804 24.3565 51.6805 24.3565 51.5371C24.3565 51.3937 24.3126 51.2662 24.225 51.1547C24.1453 51.0352 24.0258 50.9754 23.8665 50.9754H22.8625V52.063H23.8665C24.0258 52.063 24.1453 52.0112 24.225 51.9076ZM22.8625 54.0589V55.2301H23.974C24.1573 55.2301 24.2927 55.1704 24.3804 55.0508C24.476 54.9234 24.5238 54.7839 24.5238 54.6325V54.6206C24.5238 54.4692 24.472 54.3377 24.3684 54.2262C24.2728 54.1147 24.1413 54.0589 23.974 54.0589H22.8625ZM24.2848 57.2858H20.1974C20.1097 57.2858 20.034 57.2539 19.9703 57.1902C19.9066 57.1264 19.8747 57.0507 19.8747 56.9631V49.2425C19.8747 49.1548 19.9066 49.0791 19.9703 49.0154C20.034 48.9516 20.1097 48.9198 20.1974 48.9198H24.1652C25.2966 48.9198 26.1293 49.1269 26.6631 49.5412C27.2049 49.9476 27.4758 50.5651 27.4758 51.3937V51.4296C27.4758 51.7722 27.3762 52.0789 27.177 52.3498C26.9778 52.6128 26.7587 52.804 26.5197 52.9235C26.8384 53.051 27.1053 53.2781 27.3204 53.6047C27.5435 53.9234 27.6551 54.274 27.6551 54.6565C27.6551 56.4093 26.5316 57.2858 24.2848 57.2858ZM30.5934 49.6847C31.3265 49.0951 32.2945 48.8003 33.4976 48.8003C34.7007 48.8003 35.6648 49.099 36.3899 49.6966C37.1229 50.2862 37.4894 51.1188 37.4894 52.1945V54.0589C37.4894 55.1504 37.1269 55.9831 36.4018 56.5567C35.6847 57.1224 34.7167 57.4053 33.4976 57.4053C32.2786 57.4053 31.3065 57.1224 30.5815 56.5567C29.8644 55.9831 29.5059 55.1504 29.5059 54.0589V52.1945C29.5059 51.1109 29.8684 50.2743 30.5934 49.6847ZM32.8523 54.8357C33.0116 54.9951 33.2267 55.0748 33.4976 55.0748C33.7685 55.0748 33.9837 54.9951 34.143 54.8357C34.3024 54.6764 34.382 54.4334 34.382 54.1067V52.0988C34.382 51.7801 34.3024 51.5411 34.143 51.3818C33.9837 51.2144 33.7685 51.1308 33.4976 51.1308C33.2267 51.1308 33.0116 51.2144 32.8523 51.3818C32.6929 51.5411 32.6132 51.7801 32.6132 52.0988V54.1067C32.6132 54.4334 32.6929 54.6764 32.8523 54.8357ZM46.3432 48.9198H48.2315C48.3191 48.9198 48.3948 48.9516 48.4586 49.0154C48.5223 49.0791 48.5542 49.1548 48.5542 49.2425V56.9631C48.5542 57.0507 48.5223 57.1264 48.4586 57.1902C48.3948 57.2539 48.3191 57.2858 48.2315 57.2858H46.0683C45.9806 57.2858 45.905 57.2539 45.8412 57.1902C45.7775 57.1264 45.7456 57.0507 45.7456 56.9631V53.6884L44.9568 54.9791C44.8293 55.1783 44.6819 55.2779 44.5146 55.2779H43.678C43.5107 55.2779 43.3633 55.1783 43.2358 54.9791L42.447 53.6884V56.9631C42.447 57.0507 42.4151 57.1264 42.3514 57.1902C42.2877 57.2539 42.212 57.2858 42.1243 57.2858H39.9611C39.8735 57.2858 39.7978 57.2539 39.734 57.1902C39.6703 57.1264 39.6384 57.0507 39.6384 56.9631V49.2425C39.6384 49.1548 39.6703 49.0791 39.734 49.0154C39.7978 48.9516 39.8735 48.9198 39.9611 48.9198H41.8494C42.0725 48.9198 42.2438 49.0273 42.3633 49.2425L44.0963 52.1108L45.8293 49.2425C45.9488 49.0273 46.1201 48.9198 46.3432 48.9198Z" fill="#E4E4E4" />
          <path d="M43 30H29M43 30C44.1046 30 45 30.8954 45 32V38C45 39.1046 44.1046 40 43 40H29C27.8954 40 27 39.1046 27 38V32C27 30.8954 27.8954 30 29 30M43 30V28C43 26.8954 42.1046 26 41 26M29 30V28C29 26.8954 29.8954 26 31 26M31 26V24C31 22.8954 31.8954 22 33 22H39C40.1046 22 41 22.8954 41 24V26M31 26H41" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      value: "BOM-file",
    },
    {
      name: 'technical-manual-set',
      icon: (
        <span className="px-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22 26" fill="none">
            <path
              d="M7.00033 13H15.0003M7.00033 18.3333H15.0003M17.667 25H4.33366C2.8609 25 1.66699 23.8061 1.66699 22.3333V3.66667C1.66699 2.19391 2.8609 1 4.33366 1H11.7814C12.135 1 12.4741 1.14048 12.7242 1.39052L19.9431 8.60948C20.1932 8.85952 20.3337 9.19866 20.3337 9.55229V22.3333C20.3337 23.8061 19.1398 25 17.667 25Z"
              stroke="#666666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'technical-manual',
      icon: (
        <span className="px-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22 26" fill="none">
            <path
              d="M7.00033 13H15.0003M7.00033 18.3333H15.0003M17.667 25H4.33366C2.8609 25 1.66699 23.8061 1.66699 22.3333V3.66667C1.66699 2.19391 2.8609 1 4.33366 1H11.7814C12.135 1 12.4741 1.14048 12.7242 1.39052L19.9431 8.60948C20.1932 8.85952 20.3337 9.19866 20.3337 9.55229V22.3333C20.3337 23.8061 19.1398 25 17.667 25Z"
              stroke="#666666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'technical-image',
      icon: (
        <span className="px-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22 26" fill="none">
            <path
              d="M7.00033 13H15.0003M7.00033 18.3333H15.0003M17.667 25H4.33366C2.8609 25 1.66699 23.8061 1.66699 22.3333V3.66667C1.66699 2.19391 2.8609 1 4.33366 1H11.7814C12.135 1 12.4741 1.14048 12.7242 1.39052L19.9431 8.60948C20.1932 8.85952 20.3337 9.19866 20.3337 9.55229V22.3333C20.3337 23.8061 19.1398 25 17.667 25Z"
              stroke="#666666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'installation-guidelines-set',
      icon: (
        <span className="px-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path
              d="M4.03826 2.47068L9.0018 6.28839V9.00193H6.28826L2.47055 4.03839L4.03826 2.47068ZM10.6685 9.4863C10.6685 9.46547 10.6685 9.43943 10.6685 9.41859V6.08526C10.6685 5.69464 10.4862 5.33005 10.1789 5.09568L4.76222 0.929011C4.26743 0.543595 3.5643 0.59047 3.11639 1.03318L1.03305 3.11651C0.590343 3.55922 0.543468 4.26234 0.923676 4.76234L5.09034 10.179C5.32472 10.4863 5.69451 10.6686 6.07993 10.6686H9.41326C9.43409 10.6686 9.46014 10.6686 9.48097 10.6686L14.7935 15.9759C13.7778 17.5384 13.9497 19.6478 15.3195 21.0176L21.1528 26.8509C21.8039 27.5019 22.8612 27.5019 23.5122 26.8509L26.8456 23.5176C27.4966 22.8665 27.4966 21.8092 26.8456 21.1582L21.0122 15.3248C19.6424 13.9551 17.5331 13.778 15.9706 14.7988L10.6685 9.4863ZM16.5018 16.5019C17.4237 15.5801 18.9133 15.5801 19.8351 16.5019L25.6685 22.3353L22.3351 25.6686L16.5018 19.8353C15.5799 18.9134 15.5799 17.4238 16.5018 16.5019ZM6.08514 22.7519C6.08514 22.5309 5.99734 22.319 5.84106 22.1627C5.68478 22.0064 5.47282 21.9186 5.2518 21.9186C5.03079 21.9186 4.81883 22.0064 4.66255 22.1627C4.50627 22.319 4.41847 22.5309 4.41847 22.7519C4.41847 22.9729 4.50627 23.1849 4.66255 23.3412C4.81883 23.4975 5.03079 23.5853 5.2518 23.5853C5.47282 23.5853 5.68478 23.4975 5.84106 23.3412C5.99734 23.1849 6.08514 22.9729 6.08514 22.7519ZM1.8768 20.2936C1.10076 21.0696 0.668468 22.1165 0.668468 23.2103C0.668468 25.4863 2.51743 27.3353 4.79347 27.3353C5.88722 27.3353 6.93409 26.903 7.71014 26.1269L13.0903 20.7467C12.7935 20.1634 12.606 19.5436 12.5226 18.9082L6.50701 24.9238C6.05389 25.3769 5.43409 25.6321 4.79347 25.6321C3.45493 25.6321 2.36638 24.5488 2.36638 23.2051C2.36638 22.5644 2.62159 21.9446 3.07472 21.4915L10.5174 14.054L9.3143 12.8509L1.8768 20.2936ZM25.6372 9.00193C25.6372 11.3353 24.4341 13.3821 22.6164 14.5644L23.8403 15.7884C25.9601 14.278 27.3403 11.7988 27.3403 9.00193C27.3403 7.71026 27.0435 6.4863 26.5226 5.39255C26.2935 4.91339 25.6633 4.84568 25.2883 5.22068L21.7518 8.75714C21.5956 8.91339 21.382 9.00193 21.1633 9.00193H19.8351C19.3768 9.00193 19.0018 8.62693 19.0018 8.16859V6.84568C19.0018 6.62693 19.0903 6.41339 19.2466 6.25714L22.7831 2.72068C23.1581 2.34568 23.0903 1.71547 22.6112 1.4863C21.5174 0.96547 20.2935 0.668595 19.0018 0.668595C16.0903 0.668595 13.5278 2.15818 12.0383 4.4238L13.3924 5.46547C14.5643 3.60089 16.6424 2.36651 19.0018 2.36651C19.5383 2.36651 20.0539 2.42901 20.5487 2.5488L18.0435 5.05401C17.5695 5.52797 17.3039 6.1738 17.3039 6.84568V8.16859C17.3039 9.56964 18.4393 10.6998 19.8351 10.6998H21.1581C21.8299 10.6998 22.4758 10.4342 22.9497 9.96026L25.4549 7.45505C25.5747 7.94984 25.6372 8.47068 25.6372 9.00193Z"
              fill="#666666"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'installation-guidelines',
      icon: (
        <span className="px-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path
              d="M4.03826 2.47068L9.0018 6.28839V9.00193H6.28826L2.47055 4.03839L4.03826 2.47068ZM10.6685 9.4863C10.6685 9.46547 10.6685 9.43943 10.6685 9.41859V6.08526C10.6685 5.69464 10.4862 5.33005 10.1789 5.09568L4.76222 0.929011C4.26743 0.543595 3.5643 0.59047 3.11639 1.03318L1.03305 3.11651C0.590343 3.55922 0.543468 4.26234 0.923676 4.76234L5.09034 10.179C5.32472 10.4863 5.69451 10.6686 6.07993 10.6686H9.41326C9.43409 10.6686 9.46014 10.6686 9.48097 10.6686L14.7935 15.9759C13.7778 17.5384 13.9497 19.6478 15.3195 21.0176L21.1528 26.8509C21.8039 27.5019 22.8612 27.5019 23.5122 26.8509L26.8456 23.5176C27.4966 22.8665 27.4966 21.8092 26.8456 21.1582L21.0122 15.3248C19.6424 13.9551 17.5331 13.778 15.9706 14.7988L10.6685 9.4863ZM16.5018 16.5019C17.4237 15.5801 18.9133 15.5801 19.8351 16.5019L25.6685 22.3353L22.3351 25.6686L16.5018 19.8353C15.5799 18.9134 15.5799 17.4238 16.5018 16.5019ZM6.08514 22.7519C6.08514 22.5309 5.99734 22.319 5.84106 22.1627C5.68478 22.0064 5.47282 21.9186 5.2518 21.9186C5.03079 21.9186 4.81883 22.0064 4.66255 22.1627C4.50627 22.319 4.41847 22.5309 4.41847 22.7519C4.41847 22.9729 4.50627 23.1849 4.66255 23.3412C4.81883 23.4975 5.03079 23.5853 5.2518 23.5853C5.47282 23.5853 5.68478 23.4975 5.84106 23.3412C5.99734 23.1849 6.08514 22.9729 6.08514 22.7519ZM1.8768 20.2936C1.10076 21.0696 0.668468 22.1165 0.668468 23.2103C0.668468 25.4863 2.51743 27.3353 4.79347 27.3353C5.88722 27.3353 6.93409 26.903 7.71014 26.1269L13.0903 20.7467C12.7935 20.1634 12.606 19.5436 12.5226 18.9082L6.50701 24.9238C6.05389 25.3769 5.43409 25.6321 4.79347 25.6321C3.45493 25.6321 2.36638 24.5488 2.36638 23.2051C2.36638 22.5644 2.62159 21.9446 3.07472 21.4915L10.5174 14.054L9.3143 12.8509L1.8768 20.2936ZM25.6372 9.00193C25.6372 11.3353 24.4341 13.3821 22.6164 14.5644L23.8403 15.7884C25.9601 14.278 27.3403 11.7988 27.3403 9.00193C27.3403 7.71026 27.0435 6.4863 26.5226 5.39255C26.2935 4.91339 25.6633 4.84568 25.2883 5.22068L21.7518 8.75714C21.5956 8.91339 21.382 9.00193 21.1633 9.00193H19.8351C19.3768 9.00193 19.0018 8.62693 19.0018 8.16859V6.84568C19.0018 6.62693 19.0903 6.41339 19.2466 6.25714L22.7831 2.72068C23.1581 2.34568 23.0903 1.71547 22.6112 1.4863C21.5174 0.96547 20.2935 0.668595 19.0018 0.668595C16.0903 0.668595 13.5278 2.15818 12.0383 4.4238L13.3924 5.46547C14.5643 3.60089 16.6424 2.36651 19.0018 2.36651C19.5383 2.36651 20.0539 2.42901 20.5487 2.5488L18.0435 5.05401C17.5695 5.52797 17.3039 6.1738 17.3039 6.84568V8.16859C17.3039 9.56964 18.4393 10.6998 19.8351 10.6998H21.1581C21.8299 10.6998 22.4758 10.4342 22.9497 9.96026L25.4549 7.45505C25.5747 7.94984 25.6372 8.47068 25.6372 9.00193Z"
              fill="#666666"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'installation-guidelines-static',
      icon: (
        <span className="px-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path
              d="M4.03826 2.47068L9.0018 6.28839V9.00193H6.28826L2.47055 4.03839L4.03826 2.47068ZM10.6685 9.4863C10.6685 9.46547 10.6685 9.43943 10.6685 9.41859V6.08526C10.6685 5.69464 10.4862 5.33005 10.1789 5.09568L4.76222 0.929011C4.26743 0.543595 3.5643 0.59047 3.11639 1.03318L1.03305 3.11651C0.590343 3.55922 0.543468 4.26234 0.923676 4.76234L5.09034 10.179C5.32472 10.4863 5.69451 10.6686 6.07993 10.6686H9.41326C9.43409 10.6686 9.46014 10.6686 9.48097 10.6686L14.7935 15.9759C13.7778 17.5384 13.9497 19.6478 15.3195 21.0176L21.1528 26.8509C21.8039 27.5019 22.8612 27.5019 23.5122 26.8509L26.8456 23.5176C27.4966 22.8665 27.4966 21.8092 26.8456 21.1582L21.0122 15.3248C19.6424 13.9551 17.5331 13.778 15.9706 14.7988L10.6685 9.4863ZM16.5018 16.5019C17.4237 15.5801 18.9133 15.5801 19.8351 16.5019L25.6685 22.3353L22.3351 25.6686L16.5018 19.8353C15.5799 18.9134 15.5799 17.4238 16.5018 16.5019ZM6.08514 22.7519C6.08514 22.5309 5.99734 22.319 5.84106 22.1627C5.68478 22.0064 5.47282 21.9186 5.2518 21.9186C5.03079 21.9186 4.81883 22.0064 4.66255 22.1627C4.50627 22.319 4.41847 22.5309 4.41847 22.7519C4.41847 22.9729 4.50627 23.1849 4.66255 23.3412C4.81883 23.4975 5.03079 23.5853 5.2518 23.5853C5.47282 23.5853 5.68478 23.4975 5.84106 23.3412C5.99734 23.1849 6.08514 22.9729 6.08514 22.7519ZM1.8768 20.2936C1.10076 21.0696 0.668468 22.1165 0.668468 23.2103C0.668468 25.4863 2.51743 27.3353 4.79347 27.3353C5.88722 27.3353 6.93409 26.903 7.71014 26.1269L13.0903 20.7467C12.7935 20.1634 12.606 19.5436 12.5226 18.9082L6.50701 24.9238C6.05389 25.3769 5.43409 25.6321 4.79347 25.6321C3.45493 25.6321 2.36638 24.5488 2.36638 23.2051C2.36638 22.5644 2.62159 21.9446 3.07472 21.4915L10.5174 14.054L9.3143 12.8509L1.8768 20.2936ZM25.6372 9.00193C25.6372 11.3353 24.4341 13.3821 22.6164 14.5644L23.8403 15.7884C25.9601 14.278 27.3403 11.7988 27.3403 9.00193C27.3403 7.71026 27.0435 6.4863 26.5226 5.39255C26.2935 4.91339 25.6633 4.84568 25.2883 5.22068L21.7518 8.75714C21.5956 8.91339 21.382 9.00193 21.1633 9.00193H19.8351C19.3768 9.00193 19.0018 8.62693 19.0018 8.16859V6.84568C19.0018 6.62693 19.0903 6.41339 19.2466 6.25714L22.7831 2.72068C23.1581 2.34568 23.0903 1.71547 22.6112 1.4863C21.5174 0.96547 20.2935 0.668595 19.0018 0.668595C16.0903 0.668595 13.5278 2.15818 12.0383 4.4238L13.3924 5.46547C14.5643 3.60089 16.6424 2.36651 19.0018 2.36651C19.5383 2.36651 20.0539 2.42901 20.5487 2.5488L18.0435 5.05401C17.5695 5.52797 17.3039 6.1738 17.3039 6.84568V8.16859C17.3039 9.56964 18.4393 10.6998 19.8351 10.6998H21.1581C21.8299 10.6998 22.4758 10.4342 22.9497 9.96026L25.4549 7.45505C25.5747 7.94984 25.6372 8.47068 25.6372 9.00193Z"
              fill="#666666"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'warranty-Info',
      icon: (
        <span className="px-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path
              d="M4.03826 2.47068L9.0018 6.28839V9.00193H6.28826L2.47055 4.03839L4.03826 2.47068ZM10.6685 9.4863C10.6685 9.46547 10.6685 9.43943 10.6685 9.41859V6.08526C10.6685 5.69464 10.4862 5.33005 10.1789 5.09568L4.76222 0.929011C4.26743 0.543595 3.5643 0.59047 3.11639 1.03318L1.03305 3.11651C0.590343 3.55922 0.543468 4.26234 0.923676 4.76234L5.09034 10.179C5.32472 10.4863 5.69451 10.6686 6.07993 10.6686H9.41326C9.43409 10.6686 9.46014 10.6686 9.48097 10.6686L14.7935 15.9759C13.7778 17.5384 13.9497 19.6478 15.3195 21.0176L21.1528 26.8509C21.8039 27.5019 22.8612 27.5019 23.5122 26.8509L26.8456 23.5176C27.4966 22.8665 27.4966 21.8092 26.8456 21.1582L21.0122 15.3248C19.6424 13.9551 17.5331 13.778 15.9706 14.7988L10.6685 9.4863ZM16.5018 16.5019C17.4237 15.5801 18.9133 15.5801 19.8351 16.5019L25.6685 22.3353L22.3351 25.6686L16.5018 19.8353C15.5799 18.9134 15.5799 17.4238 16.5018 16.5019ZM6.08514 22.7519C6.08514 22.5309 5.99734 22.319 5.84106 22.1627C5.68478 22.0064 5.47282 21.9186 5.2518 21.9186C5.03079 21.9186 4.81883 22.0064 4.66255 22.1627C4.50627 22.319 4.41847 22.5309 4.41847 22.7519C4.41847 22.9729 4.50627 23.1849 4.66255 23.3412C4.81883 23.4975 5.03079 23.5853 5.2518 23.5853C5.47282 23.5853 5.68478 23.4975 5.84106 23.3412C5.99734 23.1849 6.08514 22.9729 6.08514 22.7519ZM1.8768 20.2936C1.10076 21.0696 0.668468 22.1165 0.668468 23.2103C0.668468 25.4863 2.51743 27.3353 4.79347 27.3353C5.88722 27.3353 6.93409 26.903 7.71014 26.1269L13.0903 20.7467C12.7935 20.1634 12.606 19.5436 12.5226 18.9082L6.50701 24.9238C6.05389 25.3769 5.43409 25.6321 4.79347 25.6321C3.45493 25.6321 2.36638 24.5488 2.36638 23.2051C2.36638 22.5644 2.62159 21.9446 3.07472 21.4915L10.5174 14.054L9.3143 12.8509L1.8768 20.2936ZM25.6372 9.00193C25.6372 11.3353 24.4341 13.3821 22.6164 14.5644L23.8403 15.7884C25.9601 14.278 27.3403 11.7988 27.3403 9.00193C27.3403 7.71026 27.0435 6.4863 26.5226 5.39255C26.2935 4.91339 25.6633 4.84568 25.2883 5.22068L21.7518 8.75714C21.5956 8.91339 21.382 9.00193 21.1633 9.00193H19.8351C19.3768 9.00193 19.0018 8.62693 19.0018 8.16859V6.84568C19.0018 6.62693 19.0903 6.41339 19.2466 6.25714L22.7831 2.72068C23.1581 2.34568 23.0903 1.71547 22.6112 1.4863C21.5174 0.96547 20.2935 0.668595 19.0018 0.668595C16.0903 0.668595 13.5278 2.15818 12.0383 4.4238L13.3924 5.46547C14.5643 3.60089 16.6424 2.36651 19.0018 2.36651C19.5383 2.36651 20.0539 2.42901 20.5487 2.5488L18.0435 5.05401C17.5695 5.52797 17.3039 6.1738 17.3039 6.84568V8.16859C17.3039 9.56964 18.4393 10.6998 19.8351 10.6998H21.1581C21.8299 10.6998 22.4758 10.4342 22.9497 9.96026L25.4549 7.45505C25.5747 7.94984 25.6372 8.47068 25.6372 9.00193Z"
              fill="#666666"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'specification-sheet',
      icon: (
        <span className="px-8 print:px-1 print:w-[40%]">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22 26" fill="none">
            <path
              d="M7.00033 13H15.0003M7.00033 18.3333H15.0003M17.667 25H4.33366C2.8609 25 1.66699 23.8061 1.66699 22.3333V3.66667C1.66699 2.19391 2.8609 1 4.33366 1H11.7814C12.135 1 12.4741 1.14048 12.7242 1.39052L19.9431 8.60948C20.1932 8.85952 20.3337 9.19866 20.3337 9.55229V22.3333C20.3337 23.8061 19.1398 25 17.667 25Z"
              stroke="#666666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ),
    },
    {
      name: 'test-report',
      icon: (
        <span className="px-8 print:px-1 print:w-[40%]">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22 26" fill="none">
            <path
              d="M7.00033 13H15.0003M7.00033 18.3333H15.0003M17.667 25H4.33366C2.8609 25 1.66699 23.8061 1.66699 22.3333V3.66667C1.66699 2.19391 2.8609 1 4.33366 1H11.7814C12.135 1 12.4741 1.14048 12.7242 1.39052L19.9431 8.60948C20.1932 8.85952 20.3337 9.19866 20.3337 9.55229V22.3333C20.3337 23.8061 19.1398 25 17.667 25Z"
              stroke="#666666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ),
    },
  ];

  const itemAttrs = attributes['item-attributes'];
  const [addlAttr, setAddlAttr] = useState([]);


  const requiredItemAttributes = [
    'depth',
    'length',
    'width',
    'net-weight',
    'IsAS14281Compliant',
    'IsAS14281Ambulant',
    'IsIndependentLivingCompliant',
    'IsNZ41121Compliant',
  ];

  const { locale } = useParams();
  const p: any = [];
  const getValues = () => {
    requiredItemAttributes?.forEach((ra: string) => {
      const found = itemAttrs?.filter((xx: any) => xx.name === ra) || [];

      if (found && found.length > 0) {
        if (found[0].name === 'IsAS14281Compliant') {
          found[0].name = 'AS1428.1 Accessible';
        } else if (found[0].name === 'IsAS14281Ambulant') {
          found[0].name = 'AS1428.1 Ambulant';
        } else if (found[0].name === 'IsIndependentLivingCompliant') {
          found[0].name = 'Independent Living Compliant';
        } else if (found[0].name === 'IsNZ41121Compliant') {
          found[0].name = 'NZS 4121 Compliant';
        }
        if (ra !== 'IsNZ41121Compliant' && ra !== 'IsAS14281Compliant' && ra !== 'IsAS14281Ambulant') {
          p.push(found[0]);
        }
        else {
          if (locale === 'nz' && ra === 'IsNZ41121Compliant') {
            p.push(found[0]);
          }
          if (locale === 'au' && (ra === 'IsAS14281Compliant' || ra === 'IsAS14281Ambulant')) {
            p.push(found[0]);
          }
        }
      }
    });
  };

  useEffect(() => {
    p.length === 0 && getValues();
    setAddlAttr(p);
  }, []);
  const getItemName = (itemName: string | Record<string, string> | undefined | null): string => {
    if (!itemName) return '';
    if (typeof itemName === 'string') {
      return itemName;
    } else {
      return itemName['en-AU'] || itemName['en-NZ'] || '';
    }
  };

  const namesMapper = (name: string) => {
    const names: any = {
      'DAE-file': "DAE ",
      'RFA-file': "RFA ",
      'DXF-file': "DXF ",
      'OBJ-file': "OBJ ",
      'DWG-file': "DWG ",
      '3DS-file': "3DS ",
      "BOM-file": "Exploded Diagram",
      'technical-manual': "Technical Manual",
      'technical-manual-set': "Technical Manual",
      'technical-image': "Technical Image",
      'installation-guidelines-set': "Installation Guidelines",
      'installation-guidelines-static': "Installation Guidelines",
      'warranty-Info': "Warranty Info",
    }
    return names[name] || name;
  }
  const isPrototype = product?.lifecycle?.toUpperCase()?.trim() === 'PROTOTYPE';

  return (
    <>
      <div className="grid grid-cols-12 gap-1">
        <div className="col-span-12 mt-4 px-3 md:mt-6 md:px-[2rem] lg:mt-8 lg:px-8 xl:px-10">
          <Typography 
            className="!leading-snug pl-1 text-[22px] !font-medium text-black md:text-[26px]"
            id="product-overview"
          >
           Product Details
          </Typography>
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="mt-4 md:mt-8 pl-1">
              <Typography className="text-xl !font-medium !leading-[1.6rem] text-[#090909] md:text-[24px]">
              Specifications
              </Typography>



              <div className="mt-[32px] flex flex-col gap-4">
                {attributes && (() => {
                  let keys = [
                    'item-code',
                    'product-types',
                    'brand',
                    'range',
                    'colour',
                    'item-material',
                    'coating-method',
                  ];

                  if (isSanitaryware) {
                    keys = [
                      'item-code',
                      'product-types',
                      'brand',
                      'range',
                      PAN_HEIGHT,
                      PAN_PROJECTION,
                      'colour',
                      'item-material',
                      'coating-method',
                    ];
                  }

                  if (productType) {
                    keys = ['product-types', 'orientation', 'limit-per-order', 'no-of-pages', 'size'];
                  }
                  return keys.map((key, index) => {
                    const attr = key;
                    const value = attributes[key];

                    if (attr === 'product-types') {
                      return (
                        <div
                          key={index}
                          className="flex text-14 font-normal leading-6 border-b border-[#E5E5E5] pb-2 print:justify-between print:text-xs lg:gap-0 lg:text-16"
                        >
                          <span className="flex-1 capitalize text-[#4D4D4D]">
                            {attr.replaceAll('-', ' ').replace('gwa', '')}
                          </span>
                          <span className="ml-4 md:ml-20 flex-1 text-[#090909] print:ml-0 print:text-right">
                            {Array.isArray(value) ? value.join(', ') : value}
                          </span>
                        </div>
                      );
                    }

                    if (
                      isValidValue(value) &&
                      key !== ITEM_NAME &&
                      !Array.isArray(value)
                    ) {
                      return (
                        <div
                          key={`${key}${index}`}
                          className="flex text-14 font-normal leading-6 border-b border-[#E5E5E5] pb-2 lg:gap-0 lg:text-16"
                        >
                          <span className="flex-1 capitalize text-[#4D4D4D]">
                            {key === 'product-types' ? 'product type' : key === 'item-code' ? 'Product Code' : key === 'item-material' ? 'Material' : key.replaceAll('-', ' ')}
                          </span>
                          <span className="ml-4 md:ml-20 flex-1 text-[#090909]">
                            {key === 'item-code' ? (
                              <span>{value}</span>
                            ) : key === PAN_HEIGHT || key === PAN_PROJECTION ? (
                              <span>{value} mm</span>
                            ) : typeof value === 'boolean' ? (
                              <span>{value ? 'Yes' : 'No'}</span>
                            ) : (
                              <span>{safeRenderValue(value)}</span>
                            )}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  });
                })()}
                {attributes &&
                  [CAPACITY_SIZE, ITEM_TAPHOLES, TRAP_TYPE, INLET_TYPE, CLEAN_FLUSH, MOUNT_TYPE, OUTLET_TYPE, OUTLET_LENGTH, CARTRIDGE_SIZE, CARTRIDGE_TYPE, SHAPE, WASTE_POSITION, WELS_FLOWRATE_1, WELS_FLOWRATE_2, SPRAY_FUNCTIONS, RAIL_LENGTH, SHOWER_HEAD_SIZE, LITRES_PER_MINUTE, OVERFLOW, TAPHOLES, CAPACITY_TO_OVERFLOW, SURFACE_FINISH, BOWL_POSITION, 
                    INLET_CONNECTION, OUTLET_CONNECTION, NOMINAL_FACTORY_PRESSURE_SETTING, NOMINAL_FACTORY_TEMPERATURE_SETTING, VALVE_TYPE, MAXIMUM_OPERATING_PRESSURE, MAXIMUM_OPERATING_TEMPERATURE, MINIMUM_OPERATING_PRESSURE, MINIMUM_OPERATING_TEMPERATURE,
                    ISCOMPLIANT, ISAMBULANT, INDEPENDENT_LIVING_COMPLAINT, ISNZCOMPLAINT]
                  .filter((attr) => {
                    if (
                      isSanitaryware &&
                      (attr === PAN_HEIGHT || attr === PAN_PROJECTION)
                    ) {
                      return false;
                    }

                    return !(isToiletSeat && HIDDEN_FOR_TOILET_SEATS.includes(attr));
                  })
                    .map((attr: any, index: number) => {
                      const value = attributes?.[attr];
                      if (
                        isValidValue(value) &&
                        attr !== ITEM_NAME &&
                        !Array.isArray(value)
                      ) {
                        return (
                          <div key={index} className="flex text-14 font-normal leading-6 border-b border-[#E5E5E5] pb-2 lg:gap-0 lg:text-16">
                            <span className="flex-1 capitalize text-[#4D4D4D]">
                              {attr === CARTRIDGE_SIZE ? 'Cartridge Size' :
                                attr === CLEAN_FLUSH ? 'Cleanflush' :

                                  (!['bidet-suites', 'buttons-accessories', 'care-toilets', 'cisterns', 'toilet-pans', 'toilet-seats', 'toilet-suites', 'urinals'].includes(productSubCategory) && attr === WELS_FLOWRATE_1) ? getKeyWelsFowRateUpdatedKey(1) :
                                    (!['bidet-suites', 'buttons-accessories', 'care-toilets', 'cisterns', 'toilet-pans', 'toilet-seats', 'toilet-suites', 'urinals'].includes(productSubCategory) && attr === WELS_FLOWRATE_2) ? getKeyWelsFowRateUpdatedKey(2) :

                                      attr.replaceAll('-', ' ').replace('gwa', '')}
                            </span>
                            <span className="ml-4 md:ml-20 flex-1 text-[#090909]">
                              {(attr === PAN_HEIGHT ||
                                attr === PAN_PROJECTION ||
                                attr === RAIL_LENGTH ||
                                attr === OUTLET_LENGTH ||
                                attr === SHOWER_HEAD_SIZE) ?
                                `${value} mm` :
                                attr === CAPACITY_SIZE ? `${value} L` :
                                  attr === LITRES_PER_MINUTE ? `${value} L /min` :
                                    (attr === WELS_FLOWRATE_1 || attr === WELS_FLOWRATE_2)
                                      ? value.replace('L/min', '  L / min') :
                                      attr === CARTRIDGE_SIZE ? value.replace(/(\d)([a-zA-Z])/g, '$1 $2') :
                                        typeof value === 'boolean' ? (value ? 'Yes' : 'No') :
                                          safeRenderValue(value)}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })}

                {addlAttr &&
                  addlAttr.map((attr: any, index: number) => (
                    <>
                      {attr.name === 'net-weight' && isValidValue(attr.value) && (
                        <div key={index} className="flex text-14 font-normal leading-6 border-b border-[#E5E5E5] pb-2 print:text-xs lg:gap-0 lg:text-16">
                          <span className="flex-1 capitalize text-[#4D4D4D]">Net Weight</span>
                          <span className="ml-4 md:ml-20 flex-1 text-[#090909] print:ml-0 print:text-right">
                            {attr.value} kg
                          </span>
                        </div>
                      )}
                      {(
                        ((attr.name === ISCOMPLIANT || attr.name === ISNZCOMPLAINT || attr.name === INDEPENDENT_LIVING_COMPLAINT) && (
                          ['bidet-suites', 'buttons-accessories', 'care-toilets', 'cisterns', 'toilet-pans', 'toilet-seats', 'toilet-suites', 'urinals'].includes(productSubCategory) ||
                          ['basin-mixers', 'bath-shower-mixers', 'care-tapware', 'kitchen-tapware', 'laundry-tapware', 'outlets-spouts', 'tapware-assemblies', 'three-piece-tapware'].includes(productSubCategory) ||
                          ['care-showers', 'shower-heads-outlets', 'shower-systems'].includes(productSubCategory) ||
                          ['above-counter-basins', 'care-basins', 'inset-basins', 'semi-recessed-basins', 'shroud-pedestal-basins', 'under-counter-basins', 'vanity-basins', 'wall-mounted-basins'].includes(productSubCategory) ||
                          ['bathroom-holders', 'care-accessories', 'shelves-cabinets', 'soap-accessories', 'toilet-roll-accessories', 'towel-accessories'].includes(productSubCategory) ||
                          ['tubs', 'tubs-cabinets'].includes(productSubCategory) ||
                          ['sinks', 'care-sinks', 'kitchen-accessories'].includes(productSubCategory)
                        )) ||
                        (attr.name === ISAMBULANT && (
                          ['bidet-suites', 'buttons-accessories', 'care-toilets', 'cisterns', 'toilet-pans', 'toilet-seats', 'toilet-suites', 'urinals'].includes(productSubCategory) ||
                          ['bathroom-holders', 'care-accessories', 'shelves-cabinets', 'soap-accessories', 'toilet-roll-accessories', 'towel-accessories'].includes(productSubCategory) ||
                          ['kitchen-accessories'].includes(productSubCategory)
                        ))
                      ) && isValidValue(attr.value) && (
                          <div key={index} className="flex text-14 font-normal leading-6 border-b border-[#E5E5E5] pb-2 print:text-xs lg:gap-0 lg:text-16">
                            <span className="flex-1 capitalize text-[#4D4D4D]">
                              {attr.name.replaceAll('-', ' ').replace('gwa', '')}
                            </span>
                            <span className="ml-4 md:ml-20 flex-1 text-[#090909] print:ml-0 print:text-right">
                              {safeRenderValue(attr.value)}
                            </span>
                          </div>
                        )}
                    </>
                  ))}

              </div>

              

            
            </div>
            {/* {!productType && (
              <div className="mr-5 mt-16 lg:mx-5">
                <Typography className="text-[24px] !font-medium text-black">Item Codes</Typography>
                {variants.map((variant: any) => (
                  <div className="mt-4 md:mt-10 flex flex-col gap-2 text-16 leading-6" key={variant.id}>
                    <span className="font-semibold text-[#333]">{getItemName(variant.attributes['item-name'])}</span>
                    <Link href={`/${product?.slug}/p/${variant?.sku}`}>
                      <span className="font-normal text-[#666]">{variant?.attributes['item-code']}</span>
                    </Link>
                  </div>
                ))}
              </div>
            )} */}
          </div>
          {productType && attributes?.['pdf-file'] ? (
                <>
                  <Typography className="mt-[40px] text-[24px] !font-medium !leading-[1.6rem] text-[#090909] lg:max-w-full">
                    Downloads and Installation
                  </Typography>
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="flex cursor-pointer mt-10">
                    <Link
                      href={attributes?.['pdf-file']}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contents"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22 26" fill="none">
                        <path
                          d="M7.00033 13H15.0003M7.00033 18.3333H15.0003M17.667 25H4.33366C2.8609 25 1.66699 23.8061 1.66699 22.3333V3.66667C1.66699 2.19391 2.8609 1 4.33366 1H11.7814C12.135 1 12.4741 1.14048 12.7242 1.39052L19.9431 8.60948C20.1932 8.85952 20.3337 9.19866 20.3337 9.55229V22.3333C20.3337 23.8061 19.1398 25 17.667 25Z"
                          stroke="#666666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <Typography
                        className="ml-[8px] text-16 !font-semibold capitalize !leading-5 text-center whitespace-pre-wrap"
                      >
                        {attributes?.['item-name'] ? `${attributes['item-name']} PDF` : 'PDF'}
                      </Typography>
                    </Link>
                  </div>
                  </div>
                </>
              ) : (
                <>
                  {DisplayDownloadComp && (
                    <>
                      <Typography className="mt-[40px] text-[24px] !font-medium !leading-[1.6rem] text-[#090909] lg:max-w-full">
                        Downloads and Installation
                      </Typography>

                      {isPrototype && (
                        
                          <div className="w-full">
                            <Typography className="mt-6 w-full !font-bold text-[14px] text-[#000] text-justify">
                              *Product details shared before launch are subject to change. Always refer to the latest published documentation before ordering or installation.
                            </Typography>
                          </div>
                        
                      )}
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                      <>
                      <div>
                        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 pr-4">
                          {filteredNames.map((item: any, index: number) => {
                            const fileDownload = FILE_DOWNLOADS.find((file) => file.name === item.name);
                            const values = Array.isArray(item?.value) ? item.value : [item.value];

                            return values.map((value: string, valueIndex: any) => {
                              const updatedValue = value?.replace('ProductThumbnail', 'Original');
                              const filename = updatedValue?.split('/').pop()?.split('?')[0] || '';
                              return (
                                updatedValue && (
                                  <Link
                                    key={`${index}-${valueIndex}`}
                                    href={updatedValue}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download={filename}
                                    className="flex items-center gap-3 rounded-md border border-[#E5E5E5] bg-white px-2 py-3 cursor-pointer hover:border-[#bbbbbb] hover:bg-[#F3EDE9] transition-colors duration-200 no-underline"
                                  >
                                    {fileDownload ? (
                                      <div className="w-[20px] h-[32px] flex-shrink-0 flex items-center justify-center">{fileDownload.icon}</div>
                                    ) : (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        xmlnsXlink="http://www.w3.org/1999/xlink"
                                        width="32"
                                        height="32"
                                        viewBox="0 0 50 50"
                                        fill="none"
                                      >
                                        <rect width="50" height="50" fill="url(#pattern0_12104_36603)" />
                                        <defs>
                                          <pattern
                                            id="pattern0_12104_36603"
                                            patternContentUnits="objectBoundingBox"
                                            width="1"
                                            height="1"
                                          >
                                            <use xlinkHref="#image0_12104_36603" transform="scale(0.02)" />
                                          </pattern>
                                          <image
                                            id="image0_12104_36603"
                                            width="50"
                                            height="50"
                                            xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANS..."
                                          />
                                        </defs>
                                      </svg>
                                    )}
                                    <Typography
                                      className="text-[13px] !font-semibold capitalize !leading-5 text-[#333]"
                                    >
                                      {namesMapper(item.name)}
                                    </Typography>
                                  </Link>
                                )
                              );
                            });
                          })}
                        </div>
                        <div className="mt-6 flex flex-col sm:flex-row gap-3 pr-4">
                          <Button
                            size="custom"
                            Icon={ArrowDownTrayIcon}
                            iconClassName="h-[20px] w-[20px]"
                            iconPosition="left"
                            variant="secondary"
                            className="flex-1 text-[14px] justify-center whitespace-nowrap border border-[#D1D1D1] px-4 py-2.5 text-[#212121] hover:!bg-[#F3EDE9] !bg-white cursor-pointer rounded-md"
                            onClick={downloadAllFiles}
                          >
                            Download all files
                          </Button>
                          <div className="flex-1">
                            <EmailAllFilesPopup files={files} product={product} technicalImageURL={technicalImageURL} />
                          </div>
                        </div>
                        </div>
                      </>
                    </div>
                </>
                  )}
                </>
              )}
        </div>
      </div>
    </>
  );
};
export default ProductSpecificationDetails;
